import { getAuth } from 'firebase/auth';
import type {
  CliBlockSubtypeListData,
  CliBlockSubtypePromotionData,
  CliBlockSubtypePromotionRequest,
  CliBlockSubtypePromotionRollbackData,
  CliBlockSubtypePromotionRollbackRequest,
  CliPromptArtifactApplyData,
  CliPromptArtifactData,
  CliPromptArtifactValidation,
  CliResponseEnvelope,
  CliScreenExampleApplyData,
  CliScreenExampleDeleteData,
  CliScreenExampleInput,
  CliScreenExampleListData,
} from '@modules/shared-types';

export const BLOCK_SCREEN_EXAMPLE_PACKS_PATH =
  'strategyPrompts/promptDictionary/block-screen-example-packs.json';

export interface PromptRegistryPointerView {
  path?: string;
  latestVersion?: number;
  activeVersion?: number;
  disabled?: boolean;
}

export interface PromptRegistryVersionView {
  version: number;
  checksum?: string;
  size?: number;
  source?: string;
  gitSha?: string;
  author?: string;
  createdAt?: unknown;
}

export interface PromptRegistryVersionsData {
  filename: string;
  pointer: PromptRegistryPointerView | null;
  versions: PromptRegistryVersionView[];
}

export interface PromptRegistryRollbackData {
  filename: string;
  pointer: PromptRegistryPointerView | null;
}

interface ProblemPayload {
  detail?: string;
  title?: string;
  error?: string;
  validation?: CliPromptArtifactValidation;
  errors?: Array<{ field?: string; message?: string }>;
}

function getBaseUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) return import.meta.env.VITE_BUILDER_API_URL;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8081';
  if (hostname === 'dev.segmently.ai' || hostname.includes('devsegmentlyai')) {
    return 'https://dev-api.segmently.ai';
  }
  if (hostname === 'stage.segmently.ai' || hostname.includes('stagesegmentlyai')) {
    return 'https://stage-api.segmently.ai';
  }
  return 'https://api.segmently.ai';
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAuth().currentUser?.getIdToken();
  if (!token) throw new Error('User not authenticated.');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function readData<T>(response: Response): Promise<T> {
  const text = await response.text().catch(() => '');
  let payload: CliResponseEnvelope<T> | ProblemPayload | null = null;
  if (text.trim()) {
    try {
      payload = JSON.parse(text) as CliResponseEnvelope<T> | ProblemPayload;
    } catch {
      throw new Error(`Prompt sections API returned invalid JSON (HTTP ${response.status}).`);
    }
  }
  if (!response.ok) {
    const problem = (payload ?? {}) as ProblemPayload;
    const validationErrors = problem.validation?.errors?.length
      ? ` ${problem.validation.errors.join(' ')}`
      : '';
    const fieldErrors = problem.errors?.map((entry) => entry.message).filter(Boolean).join(' ');
    throw new Error(
      `${problem.detail || fieldErrors || problem.error || problem.title || `HTTP ${response.status}`}${validationErrors}`,
    );
  }
  if (!payload || !('data' in payload)) {
    throw new Error('Prompt sections API response is missing its data envelope.');
  }
  return payload.data;
}

function encodePath(path: string): string {
  return encodeURIComponent(path);
}

export class PromptSectionsAdminApiService {
  constructor(private readonly baseUrl = getBaseUrl()) {}

  async getGlobalArtifact(): Promise<CliPromptArtifactData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/admin/prompts/artifact/${encodePath(BLOCK_SCREEN_EXAMPLE_PACKS_PATH)}`,
      { headers: await authHeaders() },
    );
    return readData<CliPromptArtifactData>(response);
  }

  async validateGlobalArtifact(content: unknown): Promise<CliPromptArtifactApplyData> {
    return this.applyGlobalArtifact(content, true);
  }

  async publishGlobalArtifact(content: unknown): Promise<CliPromptArtifactApplyData> {
    return this.applyGlobalArtifact(content, false);
  }

  async listGlobalVersions(): Promise<PromptRegistryVersionsData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/admin/prompts/registry/versions/${encodePath(BLOCK_SCREEN_EXAMPLE_PACKS_PATH)}`,
      { headers: await authHeaders() },
    );
    return readData<PromptRegistryVersionsData>(response);
  }

  async rollbackGlobalVersion(toVersion: number): Promise<PromptRegistryRollbackData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/admin/prompts/registry/rollback/${encodePath(BLOCK_SCREEN_EXAMPLE_PACKS_PATH)}`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ toVersion }),
      },
    );
    return readData<PromptRegistryRollbackData>(response);
  }

  async listProjectExamples(projectId: string): Promise<CliScreenExampleListData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/admin/projects/${encodeURIComponent(projectId)}/strategies/examples`,
      { headers: await authHeaders() },
    );
    return readData<CliScreenExampleListData>(response);
  }

  async saveProjectExample(
    projectId: string,
    exampleId: string,
    input: CliScreenExampleInput,
  ): Promise<CliScreenExampleApplyData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/admin/projects/${encodeURIComponent(projectId)}/strategies/examples/${encodeURIComponent(exampleId)}`,
      {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify(input),
      },
    );
    return readData<CliScreenExampleApplyData>(response);
  }

  async deleteProjectExample(
    projectId: string,
    exampleId: string,
  ): Promise<CliScreenExampleDeleteData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/admin/projects/${encodeURIComponent(projectId)}/strategies/examples/${encodeURIComponent(exampleId)}`,
      { method: 'DELETE', headers: await authHeaders() },
    );
    return readData<CliScreenExampleDeleteData>(response);
  }

  async listProjectSubtypes(projectId: string): Promise<CliBlockSubtypeListData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/projects/${encodeURIComponent(projectId)}/strategies/subtypes`,
      { headers: await authHeaders() },
    );
    return readData<CliBlockSubtypeListData>(response);
  }

  async promoteSubtype(
    request: CliBlockSubtypePromotionRequest,
  ): Promise<CliBlockSubtypePromotionData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/admin/block-subtypes/promote`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(request),
      },
    );
    return readData<CliBlockSubtypePromotionData>(response);
  }

  async rollbackSubtype(
    request: CliBlockSubtypePromotionRollbackRequest,
  ): Promise<CliBlockSubtypePromotionRollbackData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/admin/block-subtypes/rollback`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(request),
      },
    );
    return readData<CliBlockSubtypePromotionRollbackData>(response);
  }

  private async applyGlobalArtifact(
    content: unknown,
    dryRun: boolean,
  ): Promise<CliPromptArtifactApplyData> {
    const response = await fetch(
      `${this.baseUrl}/api/cli/v1/admin/prompts/artifact/${encodePath(BLOCK_SCREEN_EXAMPLE_PACKS_PATH)}`,
      {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify({ content, dryRun }),
      },
    );
    return readData<CliPromptArtifactApplyData>(response);
  }
}

export function createPromptSectionsAdminApiService(baseUrl?: string): PromptSectionsAdminApiService {
  return new PromptSectionsAdminApiService(baseUrl);
}
